import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { AuthProvider, useAuth } from "@/components/AuthContext";
import { ThemeProvider, useTheme } from "@/components/ThemeContext";
import { API_URL } from "@/lib/api";
import LoginPage from "@/components/LoginPage";
import Dashboard from "@/components/Dashboard";
import SalidasDashboard from "@/components/SalidasDashboard";
import SalidasKPI from "@/components/SalidasKPI";
import DashboardOperativo from "@/components/DashboardOperativo";
import DashboardEstrategico from "@/components/DashboardEstrategico";
import LocalStorePage from "@/components/LocalStorePage";
import CruceDatos from "@/components/CruceDatos";
import AsistenteIA from "@/components/AsistenteIA";
import UserManagement from "@/components/UserManagement";
import AuditLog from "@/components/AuditLog";
import TCBadge from "@/components/TCBadge";
import TabAnalystChat from "@/components/TabAnalystChat";
import LoteAnalysis from "@/components/LoteAnalysis";
import ComparativoLocales from "@/components/ComparativoLocales";
import Liquidacion from "@/components/Liquidacion";
import { LogOut, User, Shield, Users, Eye, Sun, Moon } from "lucide-react";

const ALL_TABS = [
  { id: "inventario", label: "Inventario", testId: "tab-inventario", roles: ["admin", "supervisor", "viewer"] },
  { id: "salidas", label: "Salidas / Entregas", testId: "tab-salidas", roles: ["admin", "supervisor"] },
  { id: "lotes", label: "Lotes FIFO", testId: "tab-lotes", roles: ["admin"] },
  { id: "comparativo", label: "Comparativo", testId: "tab-comparativo", roles: ["admin"] },
  { id: "liquidacion", label: "Liquidacion", testId: "tab-liquidacion", roles: ["admin"] },
  { id: "operativo", label: "Operativo", testId: "tab-operativo", roles: ["admin"] },
  { id: "estrategico", label: "Estrategico", testId: "tab-estrategico", roles: ["admin"] },
  { id: "kpi", label: "KPI Salidas", testId: "tab-kpi", roles: ["admin"] },
  { id: "manco_kapac", label: "Manco Kapac", testId: "tab-manco-kapac", roles: ["admin", "supervisor", "viewer"] },
  { id: "plaza_bonita", label: "Plaza Bonita", testId: "tab-plaza-bonita", roles: ["admin", "supervisor", "viewer"] },
  { id: "cruce", label: "Cruce Datos", testId: "tab-cruce", roles: ["admin", "supervisor"] },
  { id: "asistente", label: "Asistente IA", testId: "tab-asistente", roles: ["admin", "supervisor"] },
  { id: "historial", label: "Historial", testId: "tab-historial", roles: ["admin"] },
  { id: "usuarios", label: "Usuarios", testId: "tab-usuarios", roles: ["admin"] },
];

const ROLE_DISPLAY = {
  admin: { label: "Admin", icon: Shield, color: "bg-[#FF9900]/20 text-[#FF9900]" },
  supervisor: { label: "Supervisor", icon: Users, color: "bg-amber-500/20 text-amber-300" },
  viewer: { label: "Viewer", icon: Eye, color: "bg-gray-400/20 text-gray-300" },
};

function AppContent() {
  const { user, logout } = useAuth();
  const { dark, toggle } = useTheme();
  const [activeTab, setActiveTab] = useState("inventario");
  const [globalTC, setGlobalTC] = useState(null);

  const fetchGlobalTC = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/settings/tc`);
      setGlobalTC(res.data.tc);
    } catch (err) {
      console.error("Error fetching TC:", err);
    }
  }, []);

  useEffect(() => {
    if (user && user !== false) fetchGlobalTC();
  }, [user, fetchGlobalTC]);

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-muted-foreground text-lg heading-font">Cargando...</div>
      </div>
    );
  }

  if (user === false) {
    return <LoginPage />;
  }

  const role = user.role || "viewer";
  const tabs = ALL_TABS.filter((t) => t.roles.includes(role));
  const rd = ROLE_DISPLAY[role] || ROLE_DISPLAY.viewer;
  const RoleIcon = rd.icon;

  if (!tabs.find((t) => t.id === activeTab)) {
    if (tabs.length > 0 && activeTab !== tabs[0].id) {
      setActiveTab(tabs[0].id);
    }
  }

  const TAB_ANALYST_MAP = {
    inventario: "Inventario Principal",
    salidas: "Salidas / Entregas",
    lotes: "Lotes FIFO",
    comparativo: "Comparativo Locales",
    liquidacion: "Liquidacion de Inventario",
    operativo: "Dashboard Operativo",
    estrategico: "Dashboard Estrategico",
    kpi: "KPI Salidas",
    manco_kapac: "Camila - Manco Kapac",
    plaza_bonita: "Monica - Plaza Bonita",
    cruce: "Cruce de Datos",
  };
  const showAnalyst = (role === "admin" || role === "supervisor") && TAB_ANALYST_MAP[activeTab];

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-right" />

      {/* Top Navigation - Amazon Dark Blue */}
      <header className="amazon-nav sticky top-0 z-50 shadow-lg">
        {/* Primary bar: Logo + User */}
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            {/* Logo */}
            <div className="flex items-center gap-2 shrink-0">
              <span className="heading-font text-2xl font-extrabold text-white tracking-tight" data-testid="app-logo">
                i<span className="text-[#FF9900]">CATA</span>
              </span>
              <span className="hidden md:inline text-xs text-gray-400 body-font ml-2 border-l border-gray-600 pl-2">
                Gestion de Inventario
              </span>
            </div>

            {/* Right side: User info */}
            <div className="flex items-center gap-2">
              {/* Theme toggle */}
              <button
                data-testid="theme-toggle"
                onClick={toggle}
                className="p-2 rounded-md text-gray-300 hover:text-[#FF9900] hover:bg-white/5 transition-colors"
                title={dark ? "Modo claro" : "Modo oscuro"}
              >
                {dark ? <Sun className="w-4.5 h-4.5" /> : <Moon className="w-4.5 h-4.5" />}
              </button>

              {/* Role badge */}
              <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${rd.color}`} data-testid="user-role-badge">
                <RoleIcon className="w-3.5 h-3.5" />
                {rd.label}
              </div>

              {/* User name */}
              <div className="flex items-center gap-1.5 text-sm text-gray-300" data-testid="user-info">
                <User className="w-4 h-4" />
                <span className="hidden sm:inline body-font">{user.name || user.email}</span>
              </div>

              {/* Logout */}
              <button
                data-testid="logout-button"
                onClick={logout}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-400 hover:text-[#FF9900] hover:bg-white/5 rounded-md transition-colors body-font"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Salir</span>
              </button>
            </div>
          </div>
        </div>

        {/* Secondary bar: Tabs */}
        <div className="amazon-nav-sub border-t border-white/10">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-0.5 overflow-x-auto custom-scrollbar">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  data-testid={tab.testId}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2.5 text-sm font-medium transition-all whitespace-nowrap rounded-t-md body-font ${
                    activeTab === tab.id
                      ? "bg-background text-foreground"
                      : "text-gray-300 hover:text-white hover:bg-white/10"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {activeTab === "inventario" && <Dashboard userRole={role} globalTC={globalTC} onTCChange={fetchGlobalTC} />}
        {activeTab === "salidas" && <SalidasDashboard userRole={role} globalTC={globalTC} />}
        {activeTab === "lotes" && <LoteAnalysis tipoCambio={globalTC} />}
        {activeTab === "comparativo" && <ComparativoLocales tipoCambio={globalTC} />}
        {activeTab === "liquidacion" && <Liquidacion globalTC={globalTC} />}
        {activeTab === "operativo" && <DashboardOperativo globalTC={globalTC} />}
        {activeTab === "estrategico" && <DashboardEstrategico globalTC={globalTC} />}
        {activeTab === "kpi" && <SalidasKPI globalTC={globalTC} />}
        {activeTab === "manco_kapac" && <LocalStorePage localId="manco_kapac" localName="Camila - Manco Kapac" accentClass="text-amber-700" accentBg="bg-amber-100" userRole={role} globalTC={globalTC} />}
        {activeTab === "plaza_bonita" && <LocalStorePage localId="plaza_bonita" localName="Monica - Plaza Bonita" accentClass="text-violet-700" accentBg="bg-violet-100" userRole={role} globalTC={globalTC} />}
        {activeTab === "cruce" && <CruceDatos globalTC={globalTC} />}
        {activeTab === "asistente" && <AsistenteIA />}
        {activeTab === "historial" && <AuditLog />}
        {activeTab === "usuarios" && <UserManagement />}
      </main>

      {/* Tab Analyst Chat - floating */}
      {showAnalyst && (
        <TabAnalystChat
          tabName={TAB_ANALYST_MAP[activeTab]}
          tabId={activeTab}
          dataContext=""
          dataSummary=""
        />
      )}
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
